var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (_) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import React, { useEffect, useMemo } from "react";
import { useAuthDebug, useCustomFetch } from "@lululemon/auth-common";
import { SessionContext } from "./session-context";
import { useGating, useOktaAddedSubscription, useOktaErrorSubscription, useOktaExpiredSubscription, useOktaRemovedSubscription, useOktaRenewedSubscription, useSessionRefresh, useShape, useUpdateAuthState } from "./hooks";
import { getAuthSdk } from "../auth-sdk-instance";
import { AuthSdk } from "../auth-sdk";
export function SessionProvider(_a) {
    var _this = this;
    var oktaConfig = _a.oktaConfig, shapeConfig = _a.shapeConfig, pathsAuthorizationLevel = _a.pathsAuthorizationLevel, onAccessRestricted = _a.onAccessRestricted, _b = _a.env, env = _b === void 0 ? "beta" : _b, skipSessionRefresh = _a.skipSessionRefresh, skipSessionRefreshOnLoad = _a.skipSessionRefreshOnLoad, children = _a.children;
    var skipSessionRefreshOp = skipSessionRefresh || skipSessionRefreshOnLoad;
    var authSdk = useMemo(function () { return getAuthSdk({ oktaConfig: oktaConfig, env: env }); }, [env, oktaConfig]);
    var oktaAuth = authSdk.authClient;
    useEffect(function () {
        authSdk.authClient.start();
        return function () {
            authSdk.authClient.stop();
        };
    }, [authSdk.authClient]);
    useAuthDebug();
    useCustomFetch();
    var _c = useShape(__assign({ authSdk: authSdk, skipShapeOnLoad: shapeConfig.skipShapeOnLoad }, shapeConfig)), shapeStatus = _c.shapeStatus, loadShape = _c.loadShape, unloadShape = _c.unloadShape;
    var _d = useUpdateAuthState({ oktaAuth: oktaAuth }), authState = _d.authState, updateAuth = _d.updateAuth;
    var authenticationStatus = authState.authenticationStatus;
    useOktaExpiredSubscription({ oktaAuth: oktaAuth, onTokenExpired: updateAuth });
    useOktaRemovedSubscription({ oktaAuth: oktaAuth, onTokenRemoved: updateAuth });
    useOktaAddedSubscription({ oktaAuth: oktaAuth, onTokenAdded: updateAuth });
    useOktaRenewedSubscription({ oktaAuth: oktaAuth, onTokenRenewed: updateAuth });
    useOktaErrorSubscription({ oktaAuth: oktaAuth, onTokenError: updateAuth });
    var _e = useSessionRefresh({
        authenticationStatus: authenticationStatus,
        oktaConfig: oktaConfig,
        shapeConfig: shapeConfig,
        authSdk: authSdk,
        skipSessionRefresh: skipSessionRefreshOp,
        updateAuth: updateAuth
    }), sessionRefreshStatus = _e.sessionRefreshStatus, isSessionProcessing = _e.isSessionProcessing;
    var accessLevel = useGating({
        pathsAuthorizationLevel: pathsAuthorizationLevel,
        sessionAuthorizationLevel: authState.authorizationLevel,
        onAccessRestricted: onAccessRestricted,
        isSessionProcessing: isSessionProcessing
    }).accessLevel;
    var session = __assign(__assign({}, authState), { sessionRefreshStatus: sessionRefreshStatus, authenticationStatus: authenticationStatus, accessLevel: accessLevel, isSessionProcessing: isSessionProcessing, shapeStatus: shapeStatus, loadShape: loadShape, unloadShape: unloadShape, forceReloadShape: function () { return loadShape(true); }, signOut: function (href) { return __awaiter(_this, void 0, void 0, function () {
            var err_1;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        _a.trys.push([0, 4, , 5]);
                        return [4 /*yield*/, loadShape()];
                    case 1:
                        _a.sent();
                        return [4 /*yield*/, authSdk.authClient.signOut()];
                    case 2:
                        _a.sent();
                        AuthSdk.removeIdentifiers();
                        return [4 /*yield*/, updateAuth()];
                    case 3:
                        _a.sent();
                        if (href) {
                            window.location.href = href;
                        }
                        return [3 /*break*/, 5];
                    case 4:
                        err_1 = _a.sent();
                        console.log("SessionProvider logout error: ", err_1);
                        return [3 /*break*/, 5];
                    case 5: return [2 /*return*/];
                }
            });
        }); } });
    return (<SessionContext.Provider value={session}>
      {children}
    </SessionContext.Provider>);
}
